import { useState, useMemo, useEffect, useCallback } from "react"
import { PaginationState, RowSelectionState } from "@tanstack/react-table"
import H1 from "~/ui-rtk/components/ui/typography/H1"
import {
  Button,
  DateRangePicker,
  MultiSelect,
} from "~/ui-rtk/components/ui/controls"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import useConnect from "./connect"
import ProductPageFunnelChart from "./components/ProductPageFunnelChart"
import ProductPageFunnelTable, {
  ALL_COLUMNS,
  CHART_ID as TABLE_CHART_ID,
} from "./components/ProductPageFunnelTable"
import BenchmarkControls from "~/ui-rtk/components/ui/controls/BenchmarkControls"
import {
  BenchmarkTimeframe,
  PlottedRow,
  PLOT_ROW_COLORS,
  MAX_PLOTTED_ROWS,
} from "../types"
import useUrlColumnKeys from "~/ui-rtk/hooks/url-column-keys"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import type { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import TableFiltersBar from "~/ui-rtk/components/ui/controls/FiltersBar/TableFiltersBar"
import useUrlColumnFilters from "~/ui-rtk/hooks/url-column-filters"
import {
  buildFunnelExportColumns,
  rateColumns,
  useBenchmarks,
  useFunnelCsvExport,
  type FunnelExportMapping,
} from "../hooks"
import useUrlSorting from "~/ui-rtk/hooks/url-sorting"
import useCubeFiltersFromColumnFilters from "~/ui-rtk/hooks/cube-filters-from-column-filters"

type SessionFilter = "TOTAL" | "ORGANIC" | "PAID" | "TRIGGERED"

const SESSION_FILTER_OPTIONS = [
  { id: "TOTAL" as SessionFilter, label: "Total" },
  { id: "ORGANIC" as SessionFilter, label: "Organic Traffic" },
  { id: "PAID" as SessionFilter, label: "Paid Traffic" },
  { id: "TRIGGERED" as SessionFilter, label: "Triggered Traffic" },
]

const URL_PREFIX = "product_page_funnel"
const CUBE = "company_web_analytics_product_page_funnel"

const FILTER_KEY_MAPPING: Record<string, string> = {
  productTitle: `${CUBE}.product_title`,
  productHandle: `${CUBE}.product_handle`,
  productViewers: `${CUBE}.product_viewers`,
  addersToCart: `${CUBE}.adders_to_cart`,
  checkouts: `${CUBE}.checkouts`,
  purchasers: `${CUBE}.purchasers`,
  estimatedRevenue: `${CUBE}.estimated_revenue`,
  estimatedPerViewValue: `${CUBE}.estimated_per_view_value`,
}

/** Visible column → CSV column(s); rates export as conversion + abandon %. */
const EXPORT_COLUMNS: FunnelExportMapping = {
  productHandle: [{ header: "Handle", member: `${CUBE}.product_handle` }],
  productTitle: [
    {
      header: "Product",
      member: `${CUBE}.product_title`,
      // The cell shows the handle for a product with no title.
      fallbackMember: `${CUBE}.product_handle`,
    },
  ],
  productViewers: [
    { header: "Product Views", member: `${CUBE}.product_viewers` },
  ],
  productToCartConversion: rateColumns(
    "Add to Cart Rate",
    `${CUBE}.adders_to_cart`,
    `${CUBE}.product_viewers`,
  ),
  addersToCart: [{ header: "Add to Cart", member: `${CUBE}.adders_to_cart` }],
  cartToCheckoutConversion: rateColumns(
    "Checkout Rate",
    `${CUBE}.checkouts`,
    `${CUBE}.adders_to_cart`,
  ),
  checkouts: [{ header: "Checkouts", member: `${CUBE}.checkouts` }],
  checkoutToPurchaseConversion: rateColumns(
    "Purchase Rate",
    `${CUBE}.purchasers`,
    `${CUBE}.checkouts`,
  ),
  purchasers: [{ header: "Purchases", member: `${CUBE}.purchasers` }],
  productToPurchaseConversion: rateColumns(
    "Overall Conversion Rate",
    `${CUBE}.purchasers`,
    `${CUBE}.product_viewers`,
  ),
  estimatedRevenue: [
    { header: "Estimated Revenue", member: `${CUBE}.estimated_revenue` },
  ],
  estimatedPerViewValue: [
    {
      header: "Estimated Per View Value",
      member: `${CUBE}.estimated_per_view_value`,
    },
  ],
}

export default function ProductPageFunnelPage() {
  const dayjs = useDate()
  const [activeSessionFilter, setActiveSessionFilter] =
    useState<SessionFilter>("TOTAL")
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  })

  // Benchmark state
  const [benchmarkEnabled, setBenchmarkEnabled] = useState(false)
  const [benchmarkTimeframe, setBenchmarkTimeframe] =
    useState<BenchmarkTimeframe>("6mo")

  // Plot rows state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [plottedRows, setPlottedRows] = useState<PlottedRow[]>([])

  const {
    period: dateRange,
    comparePeriod: compareRange,
    setPeriod,
    setComparePeriod,
  } = usePeriod()

  // Column selection
  const defaultColumnKeys = useMemo(
    () =>
      ALL_COLUMNS.filter(
        (column: TableVisualizationColumn & { disabled?: boolean }) =>
          !column.disabled,
      ).map((column: TableVisualizationColumn) => column.key),
    [],
  )

  const { columnKeys, setColumnKeys } = useUrlColumnKeys(
    URL_PREFIX,
    defaultColumnKeys,
  )

  const columnOptions = useMemo(
    () =>
      ALL_COLUMNS.map(
        (column: TableVisualizationColumn) =>
          ({
            value: column.key,
            label: column.header,
          }) as TMultiSelectOption,
      ),
    [],
  )

  const columnConfigFiltered = useMemo(
    () =>
      ALL_COLUMNS.filter(({ key }: { key: string }) =>
        (columnKeys as string[])?.includes(key),
      ).map(
        ({
          _disabled,
          ...rest
        }: TableVisualizationColumn & { _disabled?: boolean }) => rest,
      ),
    [columnKeys],
  )

  // Sorting. TableVisualization renders the sortable headers and writes the
  // chosen sort to the URL itself, but it runs with manualSorting, so clicking
  // a header does nothing until that state reaches the cube query. Read it back
  // under the table's own chart id and hand it to useConnect, which maps it to
  // `query.order` via SORT_KEY_MAPPING.
  const { sorting } = useUrlSorting(TABLE_CHART_ID)

  // Column filters
  const { columnFilters } = useUrlColumnFilters(URL_PREFIX)

  const cubeFilters = useMemo(
    () =>
      useCubeFiltersFromColumnFilters(null, columnFilters, FILTER_KEY_MAPPING),
    [columnFilters],
  )

  // Serialized so the reset effect below depends on the sort's VALUE, not the
  // array identity — useUrlSorting rebuilds the array whenever the URL params
  // object changes, which would otherwise re-fire the reset on every render.
  const sortKey = useMemo(
    () => sorting.map(({ id, desc }) => `${id}:${desc}`).join(","),
    [sorting],
  )

  // Reset selection on filter or sort change. Sorting matters here because
  // plotted rows and row selection are keyed by row INDEX into tableData — a
  // re-sort leaves those indices pointing at different products, and page 3 of
  // the old order is not page 3 of the new one.
  useEffect(() => {
    setRowSelection({})
    setPlottedRows([])
    setPaginationState(prev => ({ ...prev, pageIndex: 0 }))
  }, [activeSessionFilter, sortKey])

  const {
    isLoading: isFunnelLoading,
    funnelData,
    compareFunnelData,
    tableData,
    compareTableData,
    totalCount,
    tableQuery,
  } = useConnect(
    activeSessionFilter,
    paginationState,
    dateRange,
    sorting,
    compareRange ?? undefined,
    false,
    cubeFilters.length > 0 ? cubeFilters : undefined,
  )

  // Fetch benchmark data (only when enabled)
  const { benchmarks, isLoading: isBenchmarkLoading } = useBenchmarks(
    "product",
    activeSessionFilter,
    benchmarkTimeframe,
    benchmarkEnabled,
  )

  // Combined loading state
  const isLoading = isFunnelLoading || (benchmarkEnabled && isBenchmarkLoading)

  // Enforce max 3 row selection
  const handleRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((prev: RowSelectionState) => RowSelectionState),
    ) => {
      setRowSelection(prev => {
        const next =
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue
        const selectedKeys = Object.keys(next).filter(k => next[k])
        if (selectedKeys.length > MAX_PLOTTED_ROWS) {
          return prev
        }
        return next
      })
    },
    [],
  )

  const handlePlotRows = useCallback(() => {
    const selectedIndices = Object.keys(rowSelection)
      .filter(k => rowSelection[k])
      .map(Number)

    const rows: PlottedRow[] = selectedIndices
      .map((idx, i) => {
        const row = tableData[idx]
        if (!row) return null

        const stages = [
          { label: "Product Views", value: row.productViewers },
          { label: "Add to Cart", value: row.addersToCart },
          { label: "Checkout", value: row.checkouts },
          { label: "Purchases", value: row.purchasers },
        ]

        const compareRow = compareTableData?.[idx]
        const compareStages = compareRow
          ? [
              { label: "Product Views", value: compareRow.productViewers },
              { label: "Add to Cart", value: compareRow.addersToCart },
              { label: "Checkout", value: compareRow.checkouts },
              { label: "Purchases", value: compareRow.purchasers },
            ]
          : undefined

        return {
          id: row.productId || `product-${idx}`,
          label: row.productTitle || row.productHandle,
          color: PLOT_ROW_COLORS[i % PLOT_ROW_COLORS.length],
          stages,
          compareStages,
        }
      })
      .filter(Boolean) as PlottedRow[]

    setPlottedRows(rows)
  }, [rowSelection, tableData, compareTableData])

  const selectedCount = Object.keys(rowSelection).filter(
    k => rowSelection[k],
  ).length

  const handleChangePeriods = ({
    start,
    end,
    compareStart,
    compareEnd,
  }: {
    start: Date
    end: Date
    compareStart?: Date | null
    compareEnd?: Date | null
  }) => {
    setPeriod(dayjs(start).format(DATE_FORMAT), dayjs(end).format(DATE_FORMAT))

    if (compareStart && compareEnd) {
      setComparePeriod(
        dayjs(compareStart).format(DATE_FORMAT),
        dayjs(compareEnd).format(DATE_FORMAT),
      )
    } else {
      setComparePeriod()
    }
  }

  const dateRangeStart = useMemo(
    () =>
      dateRange?.[0] && isValidDate(dateRange[0])
        ? dayjs(dateRange[0]).toDate()
        : new Date(),
    [dateRange?.[0]],
  )

  const dateRangeEnd = useMemo(
    () =>
      dateRange?.[1] && isValidDate(dateRange[1])
        ? dayjs(dateRange[1]).toDate()
        : new Date(),
    [dateRange?.[1]],
  )

  const compareStartDate = useMemo(
    () =>
      compareRange?.[0] && isValidDate(compareRange[0])
        ? dayjs(compareRange[0]).toDate()
        : undefined,
    [compareRange?.[0]],
  )

  const compareEndDate = useMemo(
    () =>
      compareRange?.[1] && isValidDate(compareRange[1])
        ? dayjs(compareRange[1]).toDate()
        : undefined,
    [compareRange?.[1]],
  )

  // Export CSV — the same worker export the Page Path report uses. It re-runs
  // the table's OWN request (from useConnect) exactly as fired, current page
  // included, so the file holds the rows on screen under the same filters,
  // sort and compare period; visible columns are translated to the raw
  // members / derived rates the worker can read off a Cube row.
  const exportColumns = useMemo(
    () => buildFunnelExportColumns(columnConfigFiltered, EXPORT_COLUMNS),
    [columnConfigFiltered],
  )
  const {
    exportCSV: handleExportCSV,
    isExporting,
    exportProgress,
  } = useFunnelCsvExport({
    tableQuery,
    columns: exportColumns,
    filename: "product-page-performance",
  })

  return (
    <div className="h-full px-4 py-2 bg-basic-black text-basic-white">
      <div className="px-4">
        <H1>Product Page Performance</H1>

        <div className="my-system-lg flex items-center justify-between">
          <div className="join">
            {SESSION_FILTER_OPTIONS.map(({ id, label }) => (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                key={id}
                title={label}
                className={cn(
                  "join-item border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md",
                  { "bg-system-blue-800": activeSessionFilter !== id },
                  { "bg-system-blue-600": activeSessionFilter === id },
                )}
                onClick={() => setActiveSessionFilter(id)}
              >
                <span
                  className={cn("font-[Lato]", {
                    "text-base text-system-gray-300 font-normal":
                      activeSessionFilter !== id,
                    "text-white font-semibold": activeSessionFilter === id,
                  })}
                >
                  {label}
                </span>
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <BenchmarkControls
              enabled={benchmarkEnabled}
              timeframe={benchmarkTimeframe}
              onEnabledChange={setBenchmarkEnabled}
              onTimeframeChange={setBenchmarkTimeframe}
            />
            <DateRangePicker
              start={dateRangeStart}
              end={dateRangeEnd}
              compareStart={compareStartDate}
              comparisonEnabled={true}
              compareEnd={compareEndDate}
              onChange={handleChangePeriods}
            />
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Funnel Chart Section */}
        <div className="px-4">
          <div className="bg-system-blue-800 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Conversion Funnel</h2>
            <ProductPageFunnelChart
              data={funnelData}
              compareData={compareFunnelData}
              isLoading={isLoading}
              benchmarks={benchmarks}
              showBenchmarks={benchmarkEnabled}
              plottedRows={plottedRows}
            />
          </div>
        </div>
        {/* Table Section */}
        <div className="rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              Product Performance Details
            </h2>
            <div className="flex items-center gap-4">
              <MultiSelect
                label="Columns"
                options={columnOptions}
                value={columnOptions.filter((option: TMultiSelectOption) =>
                  (columnKeys as string[]).includes(option.value),
                )}
                onChange={(options: TMultiSelectOption[]) =>
                  setColumnKeys(options.map(({ value }) => value))
                }
              />
              <TableFiltersBar
                id={URL_PREFIX}
                columnConfig={columnConfigFiltered}
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant={{ variant: "solid", color: "blue" }}
              className="border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md bg-system-blue-800"
              onClick={handlePlotRows}
              disabled={selectedCount === 0}
            >
              <span className="font-[Lato] text-sm">
                {`Plot Rows${selectedCount > 0 ? ` (${selectedCount})` : ""}`}
              </span>
            </Button>
            {plottedRows.length > 0 && (
              <Button
                variant={{ variant: "solid", color: "blue" }}
                className="border border-system-blue-600 h-system-2xl px-system-lg py-system-sm rounded-system-md bg-system-blue-800"
                onClick={() => {
                  setPlottedRows([])
                  setRowSelection({})
                }}
              >
                <span className="font-[Lato] text-sm">Clear</span>
              </Button>
            )}
            <span className="text-xs text-system-gray-500">
              Select up to {MAX_PLOTTED_ROWS} rows to plot on chart
            </span>
          </div>
          <ProductPageFunnelTable
            data={tableData}
            compareData={compareTableData}
            compareUniqueKey="productHandle"
            paginationState={paginationState}
            setPaginationState={setPaginationState}
            totalCount={totalCount}
            isLoading={isLoading}
            enableRowSelection={true}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            columnConfig={columnConfigFiltered}
            csvExport={handleExportCSV}
            isExporting={isExporting}
            exportProgress={exportProgress}
          />
        </div>
      </div>
    </div>
  )
}
